import { useState, useMemo, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { CaretLeft, CaretRight, Warning, Wrench, MapPin, Clock, Plus, User } from "@phosphor-icons/react";
import { toast } from "sonner";
import { computeRowBars, rowGridTemplateRows, BAR_HEIGHT_PX } from "../lib/calendarLayout";
import ComplianceBadge from "./ComplianceBadge";
import { fmtDate } from "../lib/dateUtil";

// Phase 270 — Unified status colour system. Status mode now uses light blue
// tint + status dot (matching DashboardCalendar / /calendar).
const STATUS_DOT_HEX = {
  scheduled: "#1d4ed8",     // dark blue
  in_progress: "#ea580c",   // orange
  completed: "#10b981",     // emerald
  cancelled: "#dc2626",     // red
};
const STATUS_COLORS = {
  // Legacy keys kept for the supplier-mode legend dot rendering
  scheduled: { bg: "bg-blue-500", text: "text-white", dot: "bg-blue-500" },
  in_progress: { bg: "bg-amber-500", text: "text-white", dot: "bg-amber-500" },
  completed: { bg: "bg-emerald-500", text: "text-white", dot: "bg-emerald-500" },
  cancelled: { bg: "bg-red-400", text: "text-white", dot: "bg-red-400" },
};
const STATUS_LABELS = { scheduled: "Scheduled", in_progress: "In Progress", completed: "Completed", cancelled: "Cancelled" };
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year, month) {
  const d = new Date(year, month, 1).getDay();
  return d === 0 ? 6 : d - 1;
}

export function JobCalendar({ jobs = [], suppliers = [], onReschedule, onQuickCreate, complianceByJob = {} }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [vendorFilter, setVendorFilter] = useState("all");
  const [dragJob, setDragJob] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [colorMode, setColorMode] = useState("status"); // "status" | "vendor"
  const containerRef = useRef(null);

  const filteredJobs = useMemo(() => {
    if (vendorFilter === "all") return jobs;
    return jobs.filter(j => j.assigned_vendor_id === vendorFilter);
  }, [jobs, vendorFilter]);

  // Multi-day support: each job occupies every cell from start_date → end_date.
  // The occupancy record carries a `position` flag (single|start|middle|end)
  // and a `continuesLeft/Right` boolean for jobs that bleed past the month edges.
  const jobsByDate = useMemo(() => {
    const map = {};
    const monthStart = `${year}-${String(month + 1).padStart(2, "0")}-01`;
    const lastDay = getDaysInMonth(year, month);
    const monthEnd = `${year}-${String(month + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    filteredJobs.forEach(j => {
      const rawStart = j.schedule?.start_date;
      if (!rawStart) return;
      const rawEnd = j.schedule?.end_date || rawStart;
      const start = rawStart < monthStart ? monthStart : rawStart;
      const end = rawEnd > monthEnd ? monthEnd : rawEnd;
      if (start > end) return;
      const continuesLeft = rawStart < monthStart;
      const continuesRight = rawEnd > monthEnd;
      let cur = new Date(start + "T00:00:00");
      const stop = new Date(end + "T00:00:00");
      while (cur <= stop) {
        const ds = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(cur.getDate()).padStart(2, "0")}`;
        const isFirst = ds === start;
        const isLast = ds === end;
        const position = isFirst && isLast ? "single" : isFirst ? "start" : isLast ? "end" : "middle";
        if (!map[ds]) map[ds] = [];
        // Wrap the job in a render object so position info travels alongside
        map[ds].push({ ...j, _position: position, _continuesLeft: isFirst && continuesLeft, _continuesRight: isLast && continuesRight });
        cur.setDate(cur.getDate() + 1);
      }
    });
    return map;
  }, [filteredJobs, year, month]);

  // Conflicts: same supplier + overlapping times on same day
  const conflicts = useMemo(() => {
    const set = new Set();
    Object.entries(jobsByDate).forEach(([, dateJobs]) => {
      for (let i = 0; i < dateJobs.length; i++) {
        for (let k = i + 1; k < dateJobs.length; k++) {
          const a = dateJobs[i], b = dateJobs[k];
          if (a.assigned_vendor_id && a.assigned_vendor_id === b.assigned_vendor_id) {
            const aS = a.schedule?.start_time || "00:00", aE = a.schedule?.end_time || "23:59";
            const bS = b.schedule?.start_time || "00:00", bE = b.schedule?.end_time || "23:59";
            if (aS < bE && bS < aE) { set.add(a.id); set.add(b.id); }
          }
        }
      }
    });
    return set;
  }, [jobsByDate]);

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  // Build all 42 grid cells. Every cell carries a real ISO date even when it's
  // a spill cell from the prev/next month — so multi-day bars that wrap across
  // a month boundary still render correctly.
  const cells = [];
  const prevMonthDays = getDaysInMonth(year, month - 1);
  const prevY = month === 0 ? year - 1 : year;
  const prevM = month === 0 ? 11 : month - 1;
  for (let i = firstDay - 1; i >= 0; i--) {
    const d = prevMonthDays - i;
    cells.push({
      day: d,
      current: false,
      dateStr: `${prevY}-${String(prevM + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
    });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ day: d, current: true, dateStr, isToday: dateStr === todayStr });
  }
  const nextY = month === 11 ? year + 1 : year;
  const nextM = month === 11 ? 0 : month + 1;
  const remaining = 7 - (cells.length % 7);
  if (remaining < 7) {
    for (let d = 1; d <= remaining; d++) {
      cells.push({
        day: d,
        current: false,
        dateStr: `${nextY}-${String(nextM + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
      });
    }
  }
  // Pad to a full 6 rows so the calendar height never jumps month-to-month.
  while (cells.length < 42) {
    const idx = cells.length - (firstDay + daysInMonth) + 1;
    cells.push({
      day: idx,
      current: false,
      dateStr: `${nextY}-${String(nextM + 1).padStart(2, "0")}-${String(idx).padStart(2, "0")}`,
    });
  }

  // Build events array for the layout engine — one entry per job. The bar
  // engine handles clipping, slot assignment, and continuesLeft/Right arrows.
  const events = useMemo(() => {
    return filteredJobs
      .filter((j) => j.schedule?.start_date)
      .map((j) => ({
        id: j.id,
        startISO: j.schedule.start_date,
        endISO: j.schedule.end_date || j.schedule.start_date,
        startTime: j.schedule.start_time || "",
        job: j,
      }));
  }, [filteredJobs]);

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };
  const goToday = () => { setYear(today.getFullYear()); setMonth(today.getMonth()); };

  // Keyboard nav
  useEffect(() => {
    const handler = (e) => {
      if (!containerRef.current?.contains(document.activeElement) && document.activeElement !== document.body) return;
      if (e.key === "ArrowLeft") { e.preventDefault(); prevMonth(); }
      if (e.key === "ArrowRight") { e.preventDefault(); nextMonth(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  });

  // Drag handlers
  const handleDragStart = (e, job) => {
    setDragJob(job);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", job.id);
    e.currentTarget.style.opacity = "0.4";
  };
  const handleDragEnd = (e) => { e.currentTarget.style.opacity = "1"; setDragJob(null); setDropTarget(null); };
  const handleDragOver = (e, dateStr) => { if (!dateStr) return; e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDropTarget(dateStr); };
  const handleDragLeave = () => setDropTarget(null);
  const handleDrop = (e, dateStr) => {
    e.preventDefault(); setDropTarget(null);
    if (!dragJob || !onReschedule || !dateStr) return;
    if (dragJob.schedule?.start_date === dateStr) return;
    onReschedule(dragJob.id, dateStr);
    setDragJob(null);
  };

  const assignedVendors = useMemo(() => {
    const seen = new Map();
    jobs.forEach(j => { if (j.assigned_vendor_id && j.assigned_vendor_name && !seen.has(j.assigned_vendor_id)) seen.set(j.assigned_vendor_id, j.assigned_vendor_name); });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [jobs]);

  // Supplier color palette
  const VENDOR_PALETTE = ["#6366f1","#0ea5e9","#f97316","#8b5cf6","#14b8a6","#ec4899","#84cc16","#f43f5e","#a855f7","#06b6d4"];
  const vendorColorMap = useMemo(() => {
    const map = {};
    assignedVendors.forEach((v, i) => { map[v.id] = VENDOR_PALETTE[i % VENDOR_PALETTE.length]; });
    return map;
  }, [assignedVendors]);

  const getJobColor = (j) => {
    if (colorMode === "vendor" && j.assigned_vendor_id && vendorColorMap[j.assigned_vendor_id]) {
      // Supplier mode — solid-coloured pill with white text
      return { mode: "vendor", style: vendorColorMap[j.assigned_vendor_id] };
    }
    // Status mode — light blue tint + status dot
    const startDate = j.schedule?.start_date;
    const effectiveEnd = j.schedule?.end_date || startDate;
    const isOverdue = effectiveEnd && todayStr && effectiveEnd < todayStr && j.status !== "completed" && j.status !== "cancelled";
    const dotHex = isOverdue ? "#dc2626" : (STATUS_DOT_HEX[j.status] || STATUS_DOT_HEX.scheduled);
    return { mode: "status", dotHex };
  };

  const scheduledCount = filteredJobs.filter(j => j.schedule?.start_date).length;
  const unscheduledCount = filteredJobs.filter(j => !j.schedule?.start_date).length;
  const conflictCount = conflicts.size;

  return (
    <TooltipProvider delayDuration={200}>
      <div data-testid="job-calendar" ref={containerRef} tabIndex={-1} className="outline-none">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={prevMonth} data-testid="cal-prev"><CaretLeft size={14} /></Button>
            <h2 className="text-lg font-bold font-[Chivo] min-w-[180px] text-center">{MONTHS[month]} {year}</h2>
            <Button variant="outline" size="sm" onClick={nextMonth} data-testid="cal-next"><CaretRight size={14} /></Button>
            <Button variant="ghost" size="sm" className="text-xs" onClick={goToday} data-testid="cal-today">Today</Button>
          </div>
          <div className="flex items-center gap-3">
            <Select value={vendorFilter} onValueChange={setVendorFilter}>
              <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="cal-vendor-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Suppliers</SelectItem>
                {assignedVendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {/* Supplier filter */}
            <div className="flex border border-border rounded-md overflow-hidden" data-testid="color-mode-toggle">
              <button onClick={() => setColorMode("status")} className={`px-2.5 py-1 text-[10px] font-medium transition-colors ${colorMode === "status" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`} data-testid="color-by-status">Status</button>
              <button onClick={() => setColorMode("vendor")} className={`px-2.5 py-1 text-[10px] font-medium transition-colors ${colorMode === "vendor" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`} data-testid="color-by-vendor">Supplier</button>
            </div>
            {/* Legend */}
            <div className="hidden sm:flex items-center gap-3 text-[10px] flex-wrap">
              {colorMode === "status" ? (
                <>
                  <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-blue-100 border border-blue-200 dark:bg-blue-900/40 dark:border-blue-800" /> Jobs</span>
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: "#1d4ed8" }} />Scheduled</span>
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: "#ea580c" }} />In progress</span>
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: "#10b981" }} />Completed</span>
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: "#dc2626" }} />Overdue</span>
                </>
              ) : (
                assignedVendors.slice(0, 6).map(v => (
                  <span key={v.id} className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: vendorColorMap[v.id] }} />{v.name.split(" ")[0]}</span>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 mb-3 text-xs text-muted-foreground">
          <span>{scheduledCount} scheduled</span>
          {unscheduledCount > 0 && <span className="text-amber-600 dark:text-amber-400">{unscheduledCount} unscheduled</span>}
          {conflictCount > 0 && (
            <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
              <Warning size={12} weight="fill" /> {Math.floor(conflictCount / 2)} conflict{conflictCount > 2 ? "s" : ""}
            </span>
          )}
        </div>

        {/* Grid — weekly rows with continuous multi-day bars */}
        <div className="border border-border rounded-lg overflow-hidden bg-card" data-testid="cal-grid">
          <div className="grid grid-cols-7 border-b border-border">
            {DAYS.map(d => (
              <div key={d} className="py-2 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40">{d}</div>
            ))}
          </div>

          {/* Render 6 independent weekly rows so multi-day bars wrap across rows. */}
          {Array.from({ length: 6 }).map((_, rowIdx) => {
            const rowCells = cells.slice(rowIdx * 7, rowIdx * 7 + 7);
            const rowDays = rowCells.map(c => c.dateStr);
            const { bars, maxSlot } = computeRowBars(rowDays, events);

            // Cap visible slots so the row doesn't explode vertically.
            const MAX_VISIBLE_SLOTS = 6;
            const visibleBars = bars.filter(b => b.slot < MAX_VISIBLE_SLOTS);
            const hiddenByDay = new Map(rowDays.map(d => [d, 0]));
            bars.forEach(b => {
              if (b.slot < MAX_VISIBLE_SLOTS) return;
              for (let c = b.startCol - 1; c < b.startCol - 1 + b.span; c++) {
                const d = rowDays[c];
                hiddenByDay.set(d, (hiddenByDay.get(d) || 0) + 1);
              }
            });
            const slotsForGrid = Math.max(1, Math.min(MAX_VISIBLE_SLOTS, maxSlot + 1));

            return (
              <div
                key={rowIdx}
                className="grid grid-cols-7 relative"
                style={{ gridTemplateRows: rowGridTemplateRows(slotsForGrid) }}
                data-testid={`cal-week-row-${rowIdx}`}
              >
                {/* Day cell backgrounds — occupy every row of the weekly grid. */}
                {rowCells.map((cell, i) => {
                  const dayJobsHere = cell.dateStr ? bars.filter(b => cell.dateStr >= rowDays[b.startCol - 1] && cell.dateStr <= rowDays[b.startCol - 1 + b.span - 1]) : [];
                  const jobCount = dayJobsHere.length;
                  const hasConflict = dayJobsHere.some(b => conflicts.has(b.event.job.id));
                  const isDragOver = dropTarget === cell.dateStr;
                  const totalMins = dayJobsHere.reduce((sum, b) => {
                    const j = b.event.job;
                    const s = j.schedule?.start_time, e = j.schedule?.end_time;
                    if (!s || !e) return sum;
                    const [sh, sm] = s.split(":").map(Number);
                    const [eh, em] = e.split(":").map(Number);
                    return sum + (eh * 60 + em) - (sh * 60 + sm);
                  }, 0);
                  const totalHrs = totalMins > 0 ? (totalMins / 60).toFixed(1) : null;
                  const hiddenCount = hiddenByDay.get(cell.dateStr) || 0;

                  return (
                    <div
                      key={i}
                      style={{ gridColumn: `${i + 1}`, gridRow: `1 / -1` }}
                      className={`min-h-[160px] border-b border-r border-border last:border-r-0 p-1.5 transition-all group relative
                        ${!cell.current ? "bg-muted/20" : "hover:bg-muted/30"}
                        ${cell.isToday ? "bg-primary/5" : ""}
                        ${hasConflict ? "bg-red-50 dark:bg-red-950/20" : ""}
                        ${isDragOver && cell.current ? "bg-primary/10 ring-2 ring-inset ring-primary/40" : ""}
                      `}
                      onDragOver={e => handleDragOver(e, cell.dateStr)}
                      onDragLeave={handleDragLeave}
                      onDrop={e => handleDrop(e, cell.dateStr)}
                      data-testid={cell.dateStr && cell.current ? `cal-day-${cell.dateStr}` : undefined}
                    >
                      {/* Today top indicator bar */}
                      {cell.isToday && (
                        <div className="absolute top-0 left-0 right-0 h-[3px] bg-primary rounded-t-sm" data-testid="cal-today-bar" />
                      )}
                      <div className="flex items-center justify-between mb-0.5 px-0.5">
                        <div className="flex items-center gap-1">
                          <span className={`text-[11px] font-medium leading-none
                            ${!cell.current ? "text-muted-foreground/40" : ""}
                            ${cell.isToday ? "bg-primary text-primary-foreground rounded-full h-5 w-5 flex items-center justify-center text-[10px] font-bold" : ""}
                          `}>
                            {cell.day}
                          </span>
                          {jobCount > 0 && cell.current && (
                            <span className="text-[8px] bg-muted text-muted-foreground rounded-full px-1.5 py-0 font-semibold leading-[14px]">{jobCount}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {totalHrs && (
                            <span className="text-[8px] text-muted-foreground/70 font-mono">{totalHrs}h</span>
                          )}
                          {hasConflict && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Warning size={11} className="text-red-500" weight="fill" />
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-xs">Scheduling conflict: same supplier with overlapping times</TooltipContent>
                            </Tooltip>
                          )}
                          {cell.current && onQuickCreate && (
                            <button
                              onClick={() => onQuickCreate(cell.dateStr)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity h-4 w-4 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center"
                              data-testid={`cal-quick-add-${cell.dateStr}`}
                            >
                              <Plus size={10} className="text-primary" weight="bold" />
                            </button>
                          )}
                        </div>
                      </div>
                      {hiddenCount > 0 && (
                        <p className="absolute bottom-1 left-1.5 text-[10px] text-muted-foreground font-medium pointer-events-none">+{hiddenCount} more</p>
                      )}
                    </div>
                  );
                })}

                {/* Continuous bar overlay — each bar spans its column range and
                   sits in the same slot row across every day it covers. */}
                {visibleBars.map((b) => {
                  const j = b.event.job;
                  const jc = getJobColor(j);
                  const isConflict = conflicts.has(j.id);
                  const pillClass = jc.mode === "vendor"
                    ? `flex items-center gap-1 px-1.5 text-[11px] leading-tight truncate font-medium transition-all hover:brightness-110 cursor-grab active:cursor-grabbing text-white rounded ${isConflict ? "ring-1 ring-red-500 ring-offset-1" : ""}`
                    : `flex items-center gap-1 px-1.5 text-[11px] leading-tight font-medium transition-all hover:brightness-95 cursor-grab active:cursor-grabbing bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded ${isConflict ? "ring-1 ring-red-500 ring-offset-1" : ""}`;
                  return (
                    <Tooltip key={`${j.id}-r${rowIdx}`}>
                      <TooltipTrigger asChild>
                        <Link
                          to={`/jobs/${j.id}`}
                          draggable
                          onDragStart={e => handleDragStart(e, j)}
                          onDragEnd={handleDragEnd}
                          onClick={e => { if (dragJob) e.preventDefault(); }}
                          className={pillClass}
                          style={{
                            gridColumn: `${b.startCol} / span ${b.span}`,
                            gridRow: 2 + b.slot,
                            height: `${BAR_HEIGHT_PX - 2}px`,
                            marginLeft: b.continuesLeft ? 0 : 3,
                            marginRight: b.continuesRight ? 0 : 3,
                            marginTop: 1,
                            position: "relative",
                            zIndex: 5,
                            ...(jc.mode === "vendor" ? { backgroundColor: jc.style } : {}),
                          }}
                          data-testid={`cal-job-${j.id}${b.isMultiDay ? "-bar" : ""}`}
                        >
                          {jc.mode === "status" && (
                            <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: jc.dotHex }} data-testid={`cal-job-dot-${j.id}`} />
                          )}
                          {b.continuesLeft && <span className="opacity-60 shrink-0">‹</span>}
                          {j.schedule?.start_time && <span className="opacity-75 shrink-0">{j.schedule.start_time}</span>}
                          <span className="truncate flex-1">{j.title}</span>
                          <ComplianceBadge summary={complianceByJob[j.id]} jobId={j.id} size={10} />
                          {b.continuesRight && <span className="opacity-60 shrink-0">›</span>}
                        </Link>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[240px] p-3" data-testid={`cal-tooltip-${j.id}`}>
                        <p className="font-semibold text-xs mb-1">{j.title}</p>
                        <p className="text-[10px] text-muted-foreground font-mono mb-1">{j.reference}</p>
                        {j.assigned_vendor_name && <p className="text-[10px] flex items-center gap-1 mb-0.5"><Wrench size={10} /> {j.assigned_vendor_name}</p>}
                        {j.assigned_operative_name && <p className="text-[10px] flex items-center gap-1 mb-0.5 text-primary"><User size={10} /> {j.assigned_operative_name}</p>}
                        {(j.street_address || j.town_city) && <p className="text-[10px] flex items-center gap-1 mb-0.5 truncate"><MapPin size={10} /> {[j.street_address, j.town_city].filter(Boolean).join(", ")}</p>}
                        {j.schedule?.start_time && <p className="text-[10px] flex items-center gap-1"><Clock size={10} /> {j.schedule.start_time}{j.schedule.end_time ? ` — ${j.schedule.end_time}` : ""}</p>}
                        {j.schedule?.end_date && j.schedule.end_date !== j.schedule.start_date && (
                          <p className="text-[10px] flex items-center gap-1 text-primary mt-0.5"><Clock size={10} /> Multi-day: {fmtDate(j.schedule.start_date)} → {fmtDate(j.schedule.end_date)}</p>
                        )}
                        {isConflict && <p className="text-[10px] text-red-600 dark:text-red-400 mt-1 flex items-center gap-1 font-medium"><Warning size={10} weight="fill" /> Time conflict</p>}
                        <p className="text-[9px] text-muted-foreground mt-1.5 italic">Drag to reschedule</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Drag hint */}
        <p className="text-[10px] text-muted-foreground mt-2 text-center">
          Drag jobs to reschedule · Hover a day and click <Plus size={8} className="inline" weight="bold" /> to quick-add · Arrow keys to navigate months
        </p>

        {/* Unscheduled jobs — draggable */}
        {unscheduledCount > 0 && (
          <div className="mt-3 border border-dashed border-amber-300 dark:border-amber-700 rounded-lg p-3" data-testid="cal-unscheduled">
            <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-2">Unscheduled Jobs ({unscheduledCount}) — drag onto a day to schedule</p>
            <div className="flex flex-wrap gap-2">
              {filteredJobs.filter(j => !j.schedule?.start_date).map(j => (
                <div
                  key={j.id}
                  draggable
                  onDragStart={e => handleDragStart(e, j)}
                  onDragEnd={handleDragEnd}
                  className="text-[10px] bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800 rounded-md px-2.5 py-1.5 hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors cursor-grab active:cursor-grabbing flex items-center gap-1.5"
                  data-testid={`cal-unscheduled-${j.id}`}
                >
                  <span className="font-mono text-[9px] opacity-70">{j.reference}</span>
                  <ComplianceBadge summary={complianceByJob[j.id]} jobId={j.id} size={9} />
                  <span>{j.title}</span>
                  {j.assigned_vendor_name && <span className="text-[8px] opacity-60">· {j.assigned_vendor_name}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
